import { lazy, Suspense, useEffect, useState } from "react";
import { kmToPath, resortById, type Resort } from "../../../shared/mountains";
import { useDevicePrefs } from "../../app/devicePrefs";
import { useHousehold } from "../../app/useHousehold";
import { useMountains } from "../../data/newsQueries";
import { readPref, writePref } from "../../data/persist";
import { useRivers, useSpots } from "../../data/weatherQueries";
import { openDirections } from "../../ui/Directions";
import { Icon } from "../../ui/Icon";
import { WeatherIcon } from "../../ui/WeatherIcon";
import { NeedsLocation } from "../sky/common";
import type { WidgetDefinition, WidgetProps } from "../types";
import { fcTemp } from "../weather/WeatherWidget";
import { openSnowReport } from "./SnowReport";
import type { MapPin } from "./TopoMap";
import { contourStep, TRAIL_MINZOOM } from "./topoScale";
import type { TopoInk, TopoLayer } from "./topoStyle";
import "./outdoors.css";

// MapLibre and the contour worker are only downloaded when a trail map is on screen.
const TopoMap = lazy(() => import("./TopoMap").then((m) => ({ default: m.TopoMap })));

interface TrailMapConfig {
  view: "places" | "home";
  ink: "theme" | TopoInk;
  gauges: boolean;
}

type MapLayers = Record<TopoLayer, boolean> & { gauges: boolean; mountains: boolean };

const LAYER_BUTTONS: { key: keyof MapLayers; label: string }[] = [
  { key: "mountains", label: "Our mountains" },
  { key: "skiRuns", label: "Ski runs" },
  { key: "trails", label: "Trails" },
  { key: "hikingRoutes", label: "Marked hiking routes" },
  { key: "roads", label: "Roads & 4×4 tracks" },
  { key: "towns", label: "Towns" },
  { key: "contours", label: "Contours" },
  { key: "publicLand", label: "Parks & public land" },
  { key: "peaks", label: "Peaks" },
  { key: "gauges", label: "River gauges" },
];

/** Layer choices, remembered per screen and per map. */
function useMapLayers(instanceId: string, defaults: MapLayers): [MapLayers, (k: keyof MapLayers) => void] {
  const key = `trailLayers:${instanceId}`;
  const [layers, setLayers] = useState<MapLayers>(() => ({ ...defaults, ...readPref<Partial<MapLayers>>(key, {}) }));
  const toggle = (k: keyof MapLayers) =>
    setLayers((prev) => {
      const next = { ...prev, [k]: !prev[k] };
      writePref(key, next);
      return next;
    });
  return [layers, toggle];
}

/** The household's theme as a map ink, following theme changes (auto switches at sunrise/sunset). */
export function useThemeInk(): TopoInk {
  const read = (): TopoInk => (document.documentElement.dataset.theme === "light" ? "paper" : "night");
  const [ink, setInk] = useState(read);
  useEffect(() => {
    const mo = new MutationObserver(() => setInk(read()));
    mo.observe(document.documentElement, { attributes: true, attributeFilter: ["data-theme"] });
    return () => mo.disconnect();
  }, []);
  return ink;
}

/** A topographic map of home and the family's places: relief, contours, trails, public land, and live pins. */
function TrailMap({ instanceId, config, editing, fullscreen, openFullscreen }: WidgetProps<TrailMapConfig>) {
  const { settings } = useHousehold();
  const { reduceEffects } = useDevicePrefs();
  const themeInk = useThemeInk();
  const spots = useSpots();
  const rivers = useRivers();
  const mountains = useMountains();
  const [zoom, setZoom] = useState(10);
  const [panel, setPanel] = useState(false);
  const home = settings.location;
  const places = settings.outdoorSpots;
  const skier = settings.outdoorInterests.includes("skiing") || settings.mountains.resorts.length > 0;
  const [layers, toggleLayer] = useMapLayers(instanceId, {
    mountains: true,
    skiRuns: skier,
    trails: true,
    hikingRoutes: false,
    roads: true,
    towns: true,
    contours: true,
    publicLand: true,
    peaks: true,
    gauges: config.gauges,
  });
  // The family's ski areas, unless one of their places already marks the spot.
  const resorts = settings.mountains.resorts
    .map(resortById)
    .filter((r): r is Resort => Boolean(r))
    .filter((r) => !places.some((p) => kmToPath([p.lat, p.lon], [[r.lat, r.lon]]) < 5));
  if (!home && places.length === 0) return <NeedsLocation icon="mountain" title="The trail map needs a home location" detail="Set a general location, or add your favorite places in Admin → Outdoors." />;

  const ink = config.ink === "theme" ? themeInk : config.ink;
  const unit = settings.temperatureUnit === "F" ? "ft" : "m";
  const shownResorts = layers.mountains ? resorts : [];
  const frame = config.view === "home" && home ? [home] : [...(home ? [home] : []), ...places, ...shownResorts];
  const snow = new Map(mountains.data?.resorts.map((r) => [r.id, r]) ?? []);
  const forecasts = new Map(spots.data?.spots.map((s) => [s.spot.id, s.periods[0]]) ?? []);

  const pins: MapPin[] = [];
  if (layers.gauges) {
    for (const g of rivers.data?.gauges ?? []) {
      if (g.lat == null || g.lon == null || g.flowCfs === null) continue;
      const flow = g.flowCfs >= 100 ? Math.round(g.flowCfs).toLocaleString("en-US") : g.flowCfs.toFixed(g.flowCfs < 10 ? 1 : 0);
      pins.push({
        id: `g-${g.siteId}`,
        lat: g.lat,
        lon: g.lon,
        className: "is-gauge",
        content: (
          <span className="pin-card" title={g.name}>
            <Icon name="waves" size="0.85rem" />
            <b>{flow}</b>
            <small>cfs{g.trend === "rising" ? " ▲" : g.trend === "falling" ? " ▼" : ""}</small>
          </span>
        ),
      });
    }
  }
  for (const s of places) {
    const p = forecasts.get(s.id);
    pins.push({
      id: `s-${s.id}`,
      lat: s.lat,
      lon: s.lon,
      className: "is-spot",
      content: (
        <button type="button" className="pin-card" title={p?.detailedForecast} aria-label={`${s.name}: directions`} onClick={() => openDirections(s)} disabled={editing}>
          <span className="pin-name">{s.name}</span>
          {p && (
            <span className="pin-wx">
              <WeatherIcon kind={p.icon} isDaytime={p.isDaytime} size="1.25rem" />
              <b>{fcTemp(p, settings.temperatureUnit)}</b>
            </span>
          )}
          <Icon name="navigate" size="0.85rem" className="pin-go" />
        </button>
      ),
    });
  }
  for (const r of shownResorts) {
    const s = snow.get(r.id);
    pins.push({
      id: `r-${r.id}`,
      lat: r.lat,
      lon: r.lon,
      className: `is-spot is-resort ${s?.powder ? "is-powder" : ""}`,
      content: (
        <button
          type="button"
          className="pin-card"
          title={s ? `${r.name}: ${s.newSnow24In ?? "—"}″ new, ${s.depthIn ?? "—"}″ deep` : r.name}
          aria-label={`${r.name}: snow report and directions`}
          onClick={() => openSnowReport(r.id)}
          disabled={editing}
        >
          <Icon name="snow" size="0.95rem" />
          <span className="pin-name">{r.name}</span>
          {s && s.newSnow24In !== null && <b>{s.newSnow24In}″</b>}
        </button>
      ),
    });
  }
  if (home) pins.push({ id: "home", lat: home.lat, lon: home.lon, className: "is-home", content: <span className="pin-home" title="Home" /> });

  const step = contourStep(unit, zoom);
  const interests = settings.outdoorInterests;

  return (
    <div className={`trail-map ink-${ink}`}>
      <Suspense fallback={<div className="widget-loading" />}>
        <TopoMap ink={ink} unit={unit} lowPower={reduceEffects} interactive={!editing} frame={frame} pins={pins} layers={layers} onZoom={setZoom} />
      </Suspense>
      {!editing && (
        <div className="topo-layers">
          <button className={`map-btn ${panel ? "on" : ""}`} onClick={() => setPanel((v) => !v)} aria-expanded={panel} aria-label="Map layers">
            <Icon name="layers" size="1.15rem" />
          </button>
          {panel && (
            <div className="topo-layer-panel" role="group" aria-label="Map layers">
              {LAYER_BUTTONS.map((b) => (
                <button key={b.key} className={`chip chip-sm ${layers[b.key] ? "on" : ""}`} aria-pressed={layers[b.key]} onClick={() => toggleLayer(b.key)}>
                  {layers[b.key] && <Icon name="check" size="0.9rem" />}
                  {b.label}
                </button>
              ))}
            </div>
          )}
        </div>
      )}
      {!fullscreen && (
        <div className="topo-title">
          <button className="w-title" onClick={openFullscreen}>
            <span>Trail map</span>
            <span className="w-chev" aria-hidden="true">
              <Icon name="chevronRight" size="0.9rem" />
            </span>
          </button>
          {home?.label && <span className="w-meta">{home.label}</span>}
        </div>
      )}
      <ul className="topo-legend" aria-label="Map key">
        {step && layers.contours && (
          <li>
            <i className="key-contour" />
            {`${step[0].toLocaleString("en-US")} ${unit === "ft" ? "ft" : "m"} contours`}
          </li>
        )}
        {layers.trails && (interests.includes("biking") || interests.includes("hiking") || interests.length === 0) && (
          <li>
            <i className="key-trail" />
            Trails{zoom < TRAIL_MINZOOM ? " (zoom in)" : ""}
          </li>
        )}
        {layers.skiRuns && (
          <li>
            <i className="key-runs" />
            Ski runs{zoom < 10 ? " (zoom in)" : ""}
          </li>
        )}
        {layers.hikingRoutes && (
          <li>
            <i className="key-routes" />
            Marked routes
          </li>
        )}
        {layers.roads && (interests.includes("hunting") || interests.includes("camping")) && (
          <li>
            <i className="key-track" />
            4×4 &amp; forest roads{zoom < TRAIL_MINZOOM ? " (zoom in)" : ""}
          </li>
        )}
        {layers.publicLand && (
          <li>
            <i className="key-park" />
            Parks &amp; public land
          </li>
        )}
      </ul>
    </div>
  );
}

export const trailMapWidget: WidgetDefinition<TrailMapConfig> = {
  type: "trail-map",
  version: 1,
  name: "Trail map",
  description:
    "A topographic map of home, your places and your ski areas: shaded relief, contours, trails, marked hiking routes, ski runs, roads, towns, public land and peaks (each on or off), with live forecast, snow and river pins.",
  icon: "mountain",
  category: "weather",
  defaultSize: { w: 6, h: 8 },
  minSize: { w: 3, h: 3 },
  stackHeight: 460,
  defaults: { view: "places", ink: "theme", gauges: true },
  settings: [
    {
      key: "view",
      label: "Show",
      type: "select",
      options: [
        { value: "places", label: "Home and all our places" },
        { value: "home", label: "Close around home" },
      ],
    },
    {
      key: "ink",
      label: "Map style",
      type: "select",
      options: [
        { value: "theme", label: "Follow the theme" },
        { value: "night", label: "Night (dark)" },
        { value: "paper", label: "Paper (light)" },
      ],
    },
    { key: "gauges", label: "Pin river gauges", type: "toggle", help: "Live streamflow at the gauges shown on Rivers & creeks." },
  ],
  provider: "openfreemap",
  supportsFullscreen: true,
  Component: TrailMap,
};
